import React, { useState, useEffect } from 'react';

const GamificationLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [activeTab, setActiveTab] = useState('leaderboard');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const crmAuth = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, statsRes, feedRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/crm/gamification/leaderboard?period=${period}&limit=10`, {
          headers: { 'Authorization': `Basic ${crmAuth}` }
        }),
        fetch(`${BACKEND_URL}/api/crm/gamification/my-stats`, {
          headers: { 'Authorization': `Basic ${crmAuth}` }
        }),
        fetch(`${BACKEND_URL}/api/crm/gamification/activity-feed?limit=15`, {
          headers: { 'Authorization': `Basic ${crmAuth}` }
        })
      ]);

      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json();
        setLeaderboard(data.leaderboard || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setMyStats(data);
      }

      if (feedRes.ok) {
        const data = await feedRes.json();
        setActivityFeed(data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500';
    if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600';
    return 'bg-gray-100';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-gray-500">Loading gamification data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🏆 Staff Leaderboard & Achievements</h1>
        <p className="text-gray-600 mt-1">Track your contributions and earn badges!</p>
      </div>

      {/* My Stats Card */}
      {myStats && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-200 text-sm font-medium">Your Stats</p>
              <h2 className="text-3xl font-bold mt-1">{myStats.total_points} pts</h2>
              <p className="text-purple-200 mt-1">Rank #{myStats.rank}</p>
            </div>
            <div className="text-right">
              <div className="flex flex-wrap gap-2 justify-end">
                {(myStats.badges || []).slice(0, 5).map((badge, idx) => (
                  <span 
                    key={idx} 
                    className="text-2xl" 
                    title={badge.name}
                  >
                    {badge.icon}
                  </span>
                ))}
              </div>
              <p className="text-purple-200 text-sm mt-2">
                {(myStats.badges || []).length} badges earned
              </p>
            </div>
          </div>

          {/* Next Badge Progress */}
          {myStats.next_badges && myStats.next_badges.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-400">
              <p className="text-sm text-purple-200 mb-2">Next Badge Progress</p>
              <div className="bg-purple-500 bg-opacity-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-2">
                    <span className="text-xl">{myStats.next_badges[0].icon}</span>
                    <span className="font-medium">{myStats.next_badges[0].name}</span>
                  </span>
                  <span className="text-sm">{myStats.next_badges[0].progress}%</span>
                </div>
                <div className="w-full bg-purple-700 rounded-full h-2">
                  <div 
                    className="bg-white h-2 rounded-full transition-all duration-500"
                    style={{ width: `${myStats.next_badges[0].progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-purple-200 mt-1">
                  {myStats.next_badges[0].current} / {myStats.next_badges[0].threshold} - {myStats.next_badges[0].description}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'leaderboard' 
              ? 'bg-purple-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          🏆 Leaderboard
        </button>
        <button
          onClick={() => setActiveTab('badges')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'badges' 
              ? 'bg-purple-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          🎖️ All Badges
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'activity' 
              ? 'bg-purple-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          📊 Activity Feed
        </button>
        <button
          onClick={() => setActiveTab('points')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'points' 
              ? 'bg-purple-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          💎 Points Guide
        </button>
      </div>

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Period Filter */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">🏆 Top Contributors</h3>
            <div className="flex gap-2">
              {['all_time', 'this_month', 'this_week'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    period === p 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p === 'all_time' ? 'All Time' : p === 'this_month' ? 'This Month' : 'This Week'}
                </button>
              ))}
            </div>
          </div>

          {leaderboard.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl">🏆</span>
              <p className="mt-2">No activity recorded yet. Be the first on the leaderboard!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry, index) => (
                <div 
                  key={index}
                  className={`flex items-center p-4 rounded-xl transition-all hover:shadow-md ${
                    index < 3 ? getRankColor(entry.rank) + ' text-white' : 'bg-gray-50'
                  }`}
                >
                  <div className={`text-2xl font-bold w-12 ${index >= 3 ? 'text-gray-400' : ''}`}>
                    {getRankBadge(entry.rank)}
                  </div>
                  
                  <div className="flex-1 ml-3">
                    <p className={`font-semibold ${index < 3 ? '' : 'text-gray-900'}`}>
                      {entry.username}
                    </p>
                    <div className="flex gap-1 mt-1">
                      {(entry.badge_details || []).slice(0, 4).map((badge, bidx) => (
                        <span key={bidx} className="text-sm" title={badge.name}>
                          {badge.icon}
                        </span>
                      ))}
                      {(entry.badge_details || []).length > 4 && (
                        <span className={`text-xs ${index < 3 ? 'text-white/80' : 'text-gray-500'}`}>
                          +{entry.badge_details.length - 4}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className={`text-xl font-bold ${index >= 3 ? 'text-purple-600' : ''}`}>
                      {entry.total_points || entry.period_points}
                    </p>
                    <p className={`text-sm ${index < 3 ? 'text-white/80' : 'text-gray-500'}`}>
                      points
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === 'badges' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">🎖️ Available Badges</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: 'prayer_warrior', name: 'Prayer Warrior', icon: '🙏', description: 'Record 50+ prayer requests', threshold: 50 },
              { id: 'faithful_follower', name: 'Faithful Follower', icon: '📞', description: 'Add 100+ follow-ups', threshold: 100 },
              { id: 'miracle_witness', name: 'Miracle Witness', icon: '✨', description: 'Record 25+ answered prayers', threshold: 25 },
              { id: 'generous_steward', name: 'Generous Steward', icon: '💰', description: 'Process 100+ donations', threshold: 100 },
              { id: 'hospitality_hero', name: 'Hospitality Hero', icon: '🏠', description: 'Log 200+ visitors', threshold: 200 },
              { id: 'connection_champion', name: 'Connection Champion', icon: '👥', description: 'Add 150+ contacts', threshold: 150 },
              { id: 'early_bird', name: 'Early Bird', icon: '🌅', description: 'Log in 30 consecutive days', threshold: 30 },
              { id: 'team_player', name: 'Team Player', icon: '🤝', description: 'Complete 500+ activities', threshold: 500 }
            ].map((badge) => {
              const earned = myStats?.badges?.some(b => b.id === badge.id);
              const progress = myStats?.next_badges?.find(b => b.id === badge.id);
              
              return (
                <div 
                  key={badge.id}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    earned 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 bg-gray-50 opacity-70'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-3xl ${earned ? '' : 'grayscale'}`}>{badge.icon}</span>
                    <div>
                      <p className={`font-semibold ${earned ? 'text-purple-900' : 'text-gray-600'}`}>
                        {badge.name}
                      </p>
                      {earned && <span className="text-xs text-purple-600">✓ Earned!</span>}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{badge.description}</p>
                  
                  {!earned && progress && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{progress.current} / {badge.threshold}</span>
                        <span>{progress.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-purple-500 h-1.5 rounded-full"
                          style={{ width: `${progress.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity Feed Tab */}
      {activeTab === 'activity' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">📊 Recent Activity</h3>
            <button 
              onClick={fetchData}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              ↻ Refresh
            </button>
          </div>
          
          {activityFeed.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl">📊</span>
              <p className="mt-2">No recent activity yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activityFeed.map((activity, index) => (
                <div 
                  key={index}
                  className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-lg">
                    {activity.activity_type === 'prayer_request_created' && '🙏'}
                    {activity.activity_type === 'prayer_follow_up' && '📝'}
                    {activity.activity_type === 'prayer_answered' && '✨'}
                    {activity.activity_type === 'donation_recorded' && '💰'}
                    {activity.activity_type === 'visitor_logged' && '🏠'}
                    {activity.activity_type === 'contact_added' && '👥'}
                    {activity.activity_type === 'invoice_created' && '📄'}
                    {activity.activity_type === 'report_created' && '📊'}
                    {activity.activity_type === 'login' && '🔑'}
                    {!['prayer_request_created', 'prayer_follow_up', 'prayer_answered', 'donation_recorded', 'visitor_logged', 'contact_added', 'invoice_created', 'report_created', 'login'].includes(activity.activity_type) && '⭐'}
                  </div>
                  <div className="flex-1 ml-3">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.staff_username}</span>
                      {' '}
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500">{formatDate(activity.created_at)}</p>
                  </div>
                  <div className="text-purple-600 font-semibold">
                    +{activity.points}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Points Guide Tab */}
      {activeTab === 'points' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">💎 How to Earn Points</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { activity: 'Record a prayer request', points: 8, icon: '🙏' },
              { activity: 'Mark prayer as answered', points: 15, icon: '✨' },
              { activity: 'Add follow-up note', points: 3, icon: '📝' },
              { activity: 'Record a donation', points: 10, icon: '💰' },
              { activity: 'Log a visitor', points: 5, icon: '🏠' },
              { activity: 'Add a contact', points: 5, icon: '👥' },
              { activity: 'Create an invoice', points: 10, icon: '📄' },
              { activity: 'Approve an invoice', points: 5, icon: '✅' },
              { activity: 'Create a report', points: 12, icon: '📊' },
              { activity: 'Approve a report', points: 8, icon: '📋' },
              { activity: 'Create a project', points: 15, icon: '📁' },
              { activity: 'Daily login', points: 1, icon: '🔑' }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-gray-700">{item.activity}</span>
                </div>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-semibold">
                  +{item.points} pts
                </span>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-purple-50 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-2">💡 Pro Tips</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Regularly follow up on prayer requests to earn consistent points</li>
              <li>• Help teammates by processing donations and logging visitors</li>
              <li>• Log in daily to maintain your streak and earn the Early Bird badge</li>
              <li>• Quality over quantity - answered prayers earn the most points!</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamificationLeaderboard;
